import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import emojiData from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { FlowVariableInsertPicker } from "../../components/flow-variables";
// Removed unused variable menuIconStyle

export const NormalMessageNode = ({ data, id, selected = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const menuRef = useRef(null);
  const textareaRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const availableVariables = Array.isArray(data.availableVariables)
    ? data.availableVariables
    : [];

  // Edit popup state
  const [editText, setEditText] = useState(data.text || "");

  // Validation errors
  const [textError, setTextError] = useState("");

  // Local state for display
  const [text, setText] = useState(data.text || "");

  // Sync local state with data prop changes
  useEffect(() => {
    setText(data.text || "");
  }, [data.text]);

  // Simplified logging - only log when popup opens
  useEffect(() => {
    if (showEditPopup) {
      console.log("=== Default Message Edit Popup Opened ===", { id });
    }
  }, [showEditPopup, id]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  // Validation functions
  const validateText = (value) => {
    if (value.length > 1024) {
      setTextError("Message cannot exceed 1024 characters");
      return false;
    }
    setTextError("");
    return true;
  };

  // Handle input changes with validation
  const handleTextChange = (e) => {
    const value = e.target.value;
    setEditText(value);
    validateText(value);
  };

  const insertAtCursor = (textToInsert) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setEditText((prev) => {
        const updated = `${prev}${textToInsert}`;
        validateText(updated);
        return updated;
      });
      return;
    }

    const start = textarea.selectionStart ?? editText.length;
    const end = textarea.selectionEnd ?? editText.length;
    const currentValue = editText;

    const updated =
      currentValue.slice(0, start) + textToInsert + currentValue.slice(end);
    setEditText(updated);
    validateText(updated);

    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + textToInsert.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleBoldClick = () => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setEditText((prev) => `*${prev}*`);
      return;
    }

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const currentValue = editText;

    let updated;
    let cursorStart;

    if (start === end) {
      // Insert opening/closing marker and place cursor between the two stars
      updated =
        currentValue.slice(0, start) + "**" + currentValue.slice(end);
      cursorStart = start + 1;
    } else {
      const selected = currentValue.slice(start, end);
      updated =
        currentValue.slice(0, start) +
        `*${selected}*` +
        currentValue.slice(end);
      cursorStart = start + selected.length + 2;
    }

    setEditText(updated);
    validateText(updated);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorStart, cursorStart);
    }, 0);
  };

  const handleEmojiSelect = (emoji) => {
    insertAtCursor(emoji.native || "");
    setShowEmojiPicker(false);
  };

  const handleVariableInsert = (variable) => {
    insertAtCursor(`{{${variable}}}`);
  };

  // Save changes
  const handleSave = () => {
    // Validate all fields
    const isTextValid = validateText(editText);

    if (isTextValid) {
      // Update parent data
      if (data.onChangeText) data.onChangeText(id, editText);

      // Update local state to reflect changes immediately
      setText(editText);

      // Close popup
      setShowEditPopup(false);
      setShowMenu(false);
    }
  };

  // Cancel changes
  const handleCancel = () => {
    // Reset to current local state values
    setEditText(text || "");

    // Clear errors
    setTextError("");

    // Close popup
    setShowEditPopup(false);
    setShowMenu(false);
  };

  // Initialize edit popup
  const handleEdit = () => {
    setEditText(text || "");
    setShowEditPopup(true);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (data.onDelete) {
      data.onDelete(id);
    }
    setShowMenu(false);
  };

  const menuIconStyle = {
    position: "absolute",
    top: 6,
    right: 8,
    cursor: "pointer",
    fontSize: 12,
    color: selected ? "#1976d2" : "#6c757d",
    zIndex: 1,
    userSelect: "none",
  };

  const menuStyle = {
    position: "absolute",
    top: 30,
    right: 8,
    background: "white",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 2,
    minWidth: "100px",
  };

  const menuItemStyle = {
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "12px",
    borderBottom: "1px solid #eee",
    transition: "background-color 0.2s",
  };

  // Popup overlay styles
  const popupOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const popupStyle = {
    background: "white",
    borderRadius: "8px",
    padding: "0",
    width: "600px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const errorStyle = {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
  };

  const charCountStyle = {
    fontSize: "11px",
    color: "#6c757d",
    textAlign: "right",
    marginTop: "4px",
    marginBottom: "4px",
  };

  const BODY_PREVIEW_MAX = 80;
  const truncateForPreview = (value) =>
    value && value.length > BODY_PREVIEW_MAX
      ? `${value.slice(0, BODY_PREVIEW_MAX)}…`
      : value;

  const renderMarkdownBold = (value) => {
    if (!value) return null;
    const parts = [];
    const regex = /\*([^*]+)\*/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push(value.slice(lastIndex, match.index));
      }
      parts.push(
        <strong key={parts.length}>
          {match[1]}
        </strong>
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < value.length) {
      parts.push(value.slice(lastIndex));
    }

    return parts;
  };

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: "6px",
          padding: "8px",
          width: "160px",
          minHeight: "60px",
          boxShadow: selected
            ? "0 4px 8px rgba(25, 118, 210, 0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 8,
            cursor: "pointer",
            fontSize: 16,
            color: selected ? "#1976d2" : "#6c757d",
            zIndex: 1,
            userSelect: "none",
            fontWeight: "bold",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            height: "11px",
          }}
          onClick={() => setShowMenu(!showMenu)}
          title="Menu"
        >
          ⋮
        </div>

        {showMenu && (
          <div ref={menuRef} style={menuStyle}>
            <div
              style={menuItemStyle}
              onClick={handleEdit}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              ✏️ Edit
            </div>
            <div
              style={{ ...menuItemStyle, borderBottom: "none" }}
              onClick={handleDelete}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              🗑️ Delete
            </div>
          </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          style={{ background: selected ? "#1976d2" : "#6c757d" }}
        />
        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontWeight: "bold",
              color: selected ? "#1976d2" : "#6c757d",
              marginBottom: "8px",
              fontSize: "11px",
            }}
          >
            Message
          </div>

          {text && text.trim() ? (
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
            >
              <div
                style={{
                  cursor: "default",
                  minHeight: "24px",
                  textAlign: "left",
                  whiteSpace: "pre-wrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {renderMarkdownBold(truncateForPreview(text))}
              </div>
            </div>
          ) : (
            <div
              style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}
            >
              <div
                style={{
                  cursor: "default",
                  fontStyle: "italic",
                  textAlign: "left",
                }}
              >
                Click Edit to add message
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Popup Modal */}
      {showEditPopup &&
        createPortal(
          <div style={popupOverlayStyle} onClick={handleCancel}>
            <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
              {/* Header with title and close button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "24px 24px 16px 24px",
                  borderBottom: "1px solid #e9ecef",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "white",
                  zIndex: 1,
                  borderRadius: "8px 8px 0 0",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "#333",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  Message
                </h3>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <button
                    onClick={handleSave}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #28a745",
                      borderRadius: "4px",
                      backgroundColor: "#28a745",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #6c757d",
                      borderRadius: "4px",
                      backgroundColor: "white",
                      color: "#6c757d",
                      cursor: "pointer",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                    }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Content Area */}
              <div style={{ padding: "0 24px 24px 24px" }}>
                {/* Message Field */}
                <div style={{ marginTop: "8px", marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Message
                  </label>
                  <textarea
                    ref={textareaRef}
                    value={editText}
                    onChange={handleTextChange}
                    placeholder="Enter your message..."
                    rows={6}
                    style={{
                      ...inputStyle,
                      borderColor: textError ? "#dc3545" : "#ddd",
                      resize: "vertical",
                      minHeight: "120px",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "6px",
                      gap: "8px",
                      fontSize: "12px",
                      color: "#555",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={handleBoldClick}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        <FormatBoldIcon style={{ fontSize: 14 }} />
                        <span>Bold</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                          backgroundColor: "#ffffff",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        <InsertEmoticonIcon style={{ fontSize: 16 }} />
                        <span>Emoji</span>
                      </button>
                    </div>
                  </div>
                  <FlowVariableInsertPicker
                    customVariables={availableVariables}
                    onInsert={handleVariableInsert}
                  />
                  {showEmojiPicker && (
                    <div
                      style={{
                        position: "fixed",
                        zIndex: 10000,
                        right: 40,
                        bottom: 160,
                      }}
                    >
                      <Picker
                        data={emojiData}
                        onEmojiSelect={handleEmojiSelect}
                        theme="light"
                      />
                    </div>
                  )}
                  <div style={charCountStyle}>{editText.length}/1024</div>
                  {textError && <div style={errorStyle}>{textError}</div>}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};